import { required } from "ra-core";
import { useWatch } from "react-hook-form";
import { ArrayInput } from "@/components/admin/array-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";
import { TextArrayInput } from "@/components/admin/text-array-input";
import { TextInput } from "@/components/admin/text-input";

import { PRIORITIES, TEAM_TASK_STATUSES } from "../common/constants";
import { emptyToNull, SaleInput } from "../common/components";

export const TeamTaskInputs = () => (
  <div className="flex flex-col gap-4">
    <TextInput source="title" validate={required()} />
    <TextInput source="description" multiline rows={4} />
    <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <SelectInput
        source="status"
        choices={TEAM_TASK_STATUSES}
        validate={required()}
      />
      <SelectInput
        source="priority"
        choices={PRIORITIES}
        validate={required()}
      />
      <SaleInput source="assignee_id" />
    </div>
    <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <DateInput source="start_date" />
      <DateInput source="due_date" />
      <NumberInput source="estimate_hours" step={0.5} min={0} />
    </div>
    <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <ReferenceInput source="deal_id" reference="deals">
        <AutocompleteInput
          label="resources.team_tasks.fields.deal_id"
          optionText="name"
        />
      </ReferenceInput>
      <MilestoneInput />
    </div>
    <TextArrayInput source="tags" />
    <ArrayInput source="checklist">
      <SimpleFormIterator inline disableReordering={false}>
        <TextInput
          source="text"
          label="resources.team_tasks.fields.text"
          className="grow"
        />
        <BooleanInput source="done" label="resources.team_tasks.fields.done" />
      </SimpleFormIterator>
    </ArrayInput>
  </div>
);

/** Milestones of the selected deal: a task linked to one is one of its work items. */
const MilestoneInput = () => {
  const dealId = useWatch({ name: "deal_id" });
  return (
    <ReferenceInput
      source="milestone_id"
      reference="deal_milestones"
      filter={dealId ? { deal_id: dealId } : { "id@eq": -1 }}
      sort={{ field: "index", order: "ASC" }}
    >
      <SelectInput
        optionText="title"
        disabled={!dealId}
        emptyText="hub.common.none"
        parse={emptyToNull}
      />
    </ReferenceInput>
  );
};

/** done_at follows the status, so "done this week" and history queries are possible. */
export const transformTeamTask = (data: Record<string, any>) => ({
  ...data,
  checklist: (data.checklist ?? []).filter((item: { text?: string }) =>
    item?.text?.trim(),
  ),
  done_at:
    data.status === "done" ? (data.done_at ?? new Date().toISOString()) : null,
});
